import React, { FC, useEffect } from 'react'

import { colors, FiftyFifty, TrustPilot } from '@smarty-nx/ds'

import DataOnlySimPlans from '../../../components/DataOnlySimPlans'
import PageHead from '../../../components/PageHead'
import {
  FullWidthContainer,
  Header2,
  SiteContainer,
} from '../../../components/shared'
import spacerClasses from '../../../components/styles/spacer.module.scss'
import withApp, { AppProps } from '../../../components/withApp/withApp'
import { FEATURES } from '../../../config'
import Hero from '../../../modular-system/Hero'
import { AwardsSection } from '../../../modules/page-section'
import GroupSmileyFamily from '../../../static/images/groupSmileyFamily.svg'

type DataOnlySimPlanOwnProps = Pick<Queries.DataOnlyPlansQuery, 'awardsSection'>
type DataOnlySimPlanProps = AppProps & DataOnlySimPlanOwnProps

const DataOnlySimPlan: FC<DataOnlySimPlanProps> = (props) => {
  const {
    fetchGroupPlans,
    groupPlans: { plans },
    awardsSection,
  } = props

  useEffect(() => {
    fetchGroupPlans({})
  }, [fetchGroupPlans])

  return (
    <>
      <FullWidthContainer background={colors['UI/DarkGrey']}>
        <SiteContainer>
          <Hero
            heading={dataOnlySimContent.heading}
            subHeading={dataOnlySimContent.subHeading}
            subTitle={dataOnlySimContent.subTitle}
            benefits={dataOnlySimContent.benefits}
            imageUrl="/assets/images/landing-pages/data-sim/tablet-dongle.svg"
            imageAlt={dataOnlySimContent.heroImageAlt}
            hideButton
            subLink={undefined}
            sublinkSuffix={undefined}
            CTA={undefined}
            CTALink={undefined}
            image={undefined}
          />
        </SiteContainer>
      </FullWidthContainer>

      <SiteContainer
        className={spacerClasses.spacerTop}
        data-module-id={plans.length > 0 ? 'plan-modules-data-sim' : null}
        data-module_name="simple-plans"
        data-module_placement="products-section"
      >
        <DataOnlySimPlans plans={plans} />
      </SiteContainer>
      <FullWidthContainer background={colors['UI/DarkGrey']}>
        <SiteContainer>
          <FiftyFifty imageTextSmall inverse>
            <img
              src={dataOnlySimContent.heroImgSrc}
              alt=""
              loading="lazy"
              decoding="async"
            />
            {dataOnlySimContent.heroContent}
          </FiftyFifty>
        </SiteContainer>

        <SiteContainer>
          {dataOnlySimContent.imageTextSections
            .slice(0)
            .map((fiftyFifty, index) => (
              <FiftyFifty
                key={index}
                imageTextSmall
                inverse={Math.abs(index % 2) === 1}
              >
                <img
                  src={fiftyFifty.imageSrc}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                {fiftyFifty.content}
              </FiftyFifty>
            ))}
        </SiteContainer>
      </FullWidthContainer>

      <FullWidthContainer background={colors['UI/DarkGrey']}>
        <SiteContainer>
          <TrustPilot
            reviewTag={
              FEATURES.TRUSTPILOT_UPDATE
                ? 'SMARTY_Homepage_Reviews'
                : 'SelectedReview'
            }
            dataTestId="homepage-trustpilot-widget"
            featureTrustPilot={FEATURES.TRUSTPILOT_UPDATE}
          />
        </SiteContainer>
      </FullWidthContainer>
      <SiteContainer>
        {awardsSection && <AwardsSection {...awardsSection} />}
      </SiteContainer>
    </>
  )
}

const dataOnlySimContent = {
  heading: 'Choose the Data Only plan that’s right for you',
  subHeading:
    'Check out our Data Only plans. No calls and texts, just great value data.',
  subTitle: '',
  benefits: [
    'Easy to manage alongside your main SIM',
    'Save 10% on every SIM in a Group plan',
    'Only pay for data',
  ],
  heroContent: (
    <>
      <Header2>What is a Group?</Header2>
      <p className="margin--regular kollektif-small">
        Group plans are a great way of managing up to 8 SIMs under one account.
        Whether you’re bringing family accounts together or just adding an extra
        SIM for your tablet, save time by paying from one place and get 10% off
        each SIM.
      </p>
    </>
  ),
  heroImgSrc: GroupSmileyFamily,
  heroImageAlt: 'Group smiley family',
  seo: {
    title: 'SMARTY Data Only SIM Deals',
    description: '',
  },

  imageTextSections: [
    {
      imageSrc: '/assets/images/landing-pages/data-sim/coins.svg',
      content: (
        <>
          <Header2>10% off every SIM added</Header2>
          <p className="margin--regular kollektif-small">
            For each additional SIM added we’ll give you 10% off. Manage billing
            for all of them under one account and save yourself time and money.
          </p>
        </>
      ),
    },
  ],
}

const ConnectedDataOnlySimPlan = withApp(
  DataOnlySimPlan as FC<DataOnlySimPlanProps>
) as FC<DataOnlySimPlanOwnProps>

export default ConnectedDataOnlySimPlan

export const Head = ({ location }: { location: { pathname: string } }) => (
  <PageHead
    title={dataOnlySimContent.seo.title}
    description={dataOnlySimContent.seo.description}
    location={location}
  >
    <meta name="robots" content="noindex, nofollow" />
  </PageHead>
)
